import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BASE_URL = 'https://www.gurukrupaeducation.com/api';

const AdminTermsEditor = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  // Fetch flat terms and convert to sections + clauses
  const fetchTerms = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/get_agreement_terms.php`);
      const flatTerms = res.data;
      const grouped = {};

      flatTerms.forEach((term) => {
        const sectionName = term.section || 'General';
        if (!grouped[sectionName]) {
          grouped[sectionName] = [];
        }
        grouped[sectionName].push({
          id: term.id,               // keep original id if needed for update/delete
          clause_number: term.clause_number,
          clause_text: term.clause_text,
          sort_order: term.sort_order,
        });
      });

      // Convert to array of section objects and sort clauses by sort_order
      const sectionArray = Object.entries(grouped).map(([title, clauses]) => ({
        title,
        clauses: clauses.sort((a, b) => a.sort_order - b.sort_order),
      }));
      setSections(sectionArray);
    } catch (err) {
      console.error(err);
      setMessage('Failed to load terms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTerms();
  }, []);

  // ----- Section operations -----
  const addSection = () => {
    const newTitle = prompt('Enter new section name:');
    if (!newTitle) return;
    setSections([...sections, { title: newTitle, clauses: [] }]);
  };

  const removeSection = (idx) => {
    if (!window.confirm(`Delete section "${sections[idx].title}" and all its clauses?`)) return;
    const updated = [...sections];
    updated.splice(idx, 1);
    setSections(updated);
  };

  const moveSection = (idx, direction) => {
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === sections.length - 1) return;
    const updated = [...sections];
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    [updated[idx], updated[targetIdx]] = [updated[targetIdx], updated[idx]];
    setSections(updated);
  };

  // ----- Clause operations (inside a section) -----
  const addClause = (sectionIdx) => {
    const updated = [...sections];
    const newClause = {
      clause_text: 'New clause text',
      clause_number: (updated[sectionIdx].clauses.length + 1).toString(),
      sort_order: updated[sectionIdx].clauses.length,
    };
    updated[sectionIdx].clauses.push(newClause);
    // renumber clauses
    updated[sectionIdx].clauses = renumberClauses(updated[sectionIdx].clauses);
    setSections(updated);
  };

  const removeClause = (sectionIdx, clauseIdx) => {
    const updated = [...sections];
    updated[sectionIdx].clauses.splice(clauseIdx, 1);
    updated[sectionIdx].clauses = renumberClauses(updated[sectionIdx].clauses);
    setSections(updated);
  };

  const moveClause = (sectionIdx, clauseIdx, direction) => {
    const updated = [...sections];
    const clauses = updated[sectionIdx].clauses;
    if (direction === 'up' && clauseIdx === 0) return;
    if (direction === 'down' && clauseIdx === clauses.length - 1) return;
    const targetIdx = direction === 'up' ? clauseIdx - 1 : clauseIdx + 1;
    [clauses[clauseIdx], clauses[targetIdx]] = [clauses[targetIdx], clauses[clauseIdx]];
    updated[sectionIdx].clauses = renumberClauses(clauses);
    setSections(updated);
  };

  const updateClauseText = (sectionIdx, clauseIdx, newText) => {
    const updated = [...sections];
    updated[sectionIdx].clauses[clauseIdx].clause_text = newText;
    setSections(updated);
  };

  // Helper: renumber clauses inside a section (1,2,3...)
  const renumberClauses = (clauses) => {
    return clauses.map((clause, idx) => ({
      ...clause,
      clause_number: (idx + 1).toString(),
      sort_order: idx,
    }));
  };

  // ----- Save: flatten sections + clauses to match backend API -----
  const saveTerms = async () => {
    if (!window.confirm('This will overwrite all existing terms. Continue?')) return;

    const flatTerms = [];
    let globalOrder = 0;
    for (const section of sections) {
      for (const clause of section.clauses) {
        flatTerms.push({
          section: section.title,
          clause_number: clause.clause_number,
          clause_text: clause.clause_text,
          sort_order: globalOrder++,
        });
      }
    }

    try {
      const res = await axios.post(`${BASE_URL}/save_agreement_terms.php`, {
        terms: flatTerms,
      });
      setMessage(res.data.message || 'Terms saved!');
    } catch (err) {
      console.error(err);
      setMessage('Save failed.');
    }
  };

  if (loading) return <p>Loading terms...</p>;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2>✍️ Edit Legal Terms (Sections + Clauses)</h2>
      {message && <p style={{ color: 'green' }}><strong>{message}</strong></p>}

      <div style={{ marginBottom: '20px' }}>
        <button onClick={addSection} style={{ ...buttonStyle, background: '#2563eb' }}>
          + Add New Section
        </button>
        <button onClick={saveTerms} style={{ ...buttonStyle, background: '#16a34a', marginLeft: '10px' }}>
          💾 Save All Terms
        </button>
      </div>

      {sections.map((section, secIdx) => (
        <div key={secIdx} style={sectionCardStyle}>
          <div style={sectionHeaderStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, minWidth: '200px' }}>{section.title}</h3>
              <button onClick={() => moveSection(secIdx, 'up')} disabled={secIdx === 0} style={iconButton}>▲</button>
              <button onClick={() => moveSection(secIdx, 'down')} disabled={secIdx === sections.length-1} style={iconButton}>▼</button>
              <button onClick={() => removeSection(secIdx)} style={{ ...iconButton, color: '#dc2626' }}>🗑️ Delete Section</button>
              <button onClick={() => addClause(secIdx)} style={{ ...smallButton, background: '#eab308' }}>+ Clause</button>
            </div>
          </div>

          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ width: '70px' }}>#</th>
                <th>Clause Text</th>
                <th style={{ width: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {section.clauses.map((clause, clauseIdx) => (
                <tr key={clauseIdx}>
                  <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{clause.clause_number}</td>
                  <td>
                    <textarea
                      value={clause.clause_text}
                      onChange={(e) => updateClauseText(secIdx, clauseIdx, e.target.value)}
                      rows={2}
                      style={{ width: '100%', resize: 'vertical', fontFamily: 'monospace' }}
                    />
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button onClick={() => moveClause(secIdx, clauseIdx, 'up')} disabled={clauseIdx === 0} style={iconButton}>▲</button>
                    <button onClick={() => moveClause(secIdx, clauseIdx, 'down')} disabled={clauseIdx === section.clauses.length-1} style={iconButton}>▼</button>
                    <button onClick={() => removeClause(secIdx, clauseIdx)} style={{ ...iconButton, color: '#dc2626' }}>✕</button>
                  </td>
                </tr>
              ))}
              {section.clauses.length === 0 && (
                <tr>
                  <td colSpan="3" style={{ textAlign: 'center', color: '#888' }}>
                    No clauses yet. Click "+ Clause" to add.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ))}

      {sections.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', background: '#f3f4f6', borderRadius: '12px' }}>
          <p>No sections defined. Click "Add New Section" to start building your legal document.</p>
        </div>
      )}
    </div>
  );
};

// ------------------ Styles ------------------
const buttonStyle = {
  padding: '8px 16px',
  border: 'none',
  borderRadius: '6px',
  color: 'white',
  fontWeight: 'bold',
  cursor: 'pointer',
  fontSize: '14px',
};

const iconButton = {
  background: '#e5e7eb',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  padding: '4px 8px',
  fontSize: '14px',
  margin: '0 2px',
};

const smallButton = {
  background: '#6b7280',
  border: 'none',
  borderRadius: '4px',
  color: 'white',
  cursor: 'pointer',
  padding: '4px 10px',
  fontSize: '12px',
  fontWeight: 'bold',
};

const sectionCardStyle = {
  border: '1px solid #ddd',
  borderRadius: '12px',
  marginBottom: '24px',
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const sectionHeaderStyle = {
  background: '#f9fafb',
  padding: '16px',
  borderBottom: '1px solid #ddd',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
};

export default AdminTermsEditor;